import { ViewType, ViewObject } from '../base';

import { take } from 'rxjs';

import { AppConstants } from '@unpispas/upp-defs';
import { adhocService } from '@unpispas/upp-base';
import { clockService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { alertService } from '@unpispas/upp-base';
import { Configuration, configService } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';

import { dataService } from '../../data';

import { Place } from '../objects/place';
import { Client } from '../objects/client';
import { FloorArea } from '../objects/floorarea';
import { QrCode } from '../objects/qrcode';
import { RasppiConnect } from '../objects/rasppiconnect';
import { PrintDevice } from '../objects/printer';
import { PriceTime } from '../objects/pricetime';
import { Pinpad } from '../objects/pinpad';
import { Session } from '../objects/session';

import type { UserView } from './user';
import type { PayRequest, QrCodeView } from './qrcode';
import type { ProductView } from './product';
import type { PreselectView } from './preselect';
import type { EmployeeView } from './employee';
import type { OfferView } from './offer';
import type { ExtraView } from './extra';
import type { DiscountView } from './discount';
import type { FamilyView } from './family';
import type { TicketView } from './ticket';
import type { RasppiView } from './rasppi';
import type { TillView } from './till';
import type { PayAccountView } from './payaccount';
import type { AuditView } from './audit';
import type { PriceZoneView } from './pricezone';
import type { Payment } from '../objects/payment';
import type { Stripe } from '../objects/stripe';
import { SearchTool } from './search';
import { Catalog } from './catalog';


/*
    TICKET NUMERATION:

    LOGIN (POS) assigns series/invceno on the device from the local ticket store
    (`_DEVICE_TICKETS`), reconciled with device/getticket via Math.max(local, server)
    before the first fiscal emission (T/R/F/A/C). Series X may use local without waiting.
    GUEST (QR) must not use that store: the server assigns numbers
    (get_ticket_invoice / _TICKETINVOICE).

    - SERIES:
      * X: Provisional (unpaid) ticket serial number
      * S: Ticket serial number
      * C: Ticket with invoice numeration
      * F: Invoice numeration
      * R: Rectification numeration
    
    - INVOICE: 
      * X: Provisional (unpaid) ticket numeration
      * T: Ticket numeration
      * F: Invoice numeration
      * R: Rectification numeration
      * A: Recapitulative invoice
      * C: Ticket with invoice numeration    
*/

export enum SerialType {
    X = 'X', // X: Provisional (unpaid) ticket serial number
    S = 'S', // S: Ticket serial number
    C = 'C', // C: Ticket with invoice numeration
    F = 'F', // F: Invoice numeration
    R = 'R', // R: Rectification numeration
    A = 'A', // A: Recapitulative invoice serial number
};

export enum InvoiceType {
    X = 'X', // X: Provisional (unpaid) ticket numeration
    T = 'T', // T: Ticket numeration
    F = 'F', // F: Invoice numeration
    R = 'R', // R: Rectification numeration
    A = 'A', // A: Recapitulative invoice
    C = 'C'  // C: Ticket with invoice numeration   
};    

export type GroupedInfo = {
    parent: ProductView | null,
    children: ProductView[]
}
export type CatalogGrouped = GroupedInfo[];

interface TicketDeviceInfo {
    X: number | null,    // last provisional (unpaid) ticket number
    T: number | null,    // last ticket number 
    F: number | null,    // last invoice number
    R: number | null,    // last rectificative number
    A: number | null,    // last ticket (recapitulative) number
    C: number | null,    // last ticket (with invoice) number

    /** Last TicketBAI of this device serial (LOGIN wipe rehydration). */
    tbai: {
        created: string | null;
        series: string | null;
        invoice: string | null;
        signature: string | null;
    } | null;

    /** Last VeriFactu RF of this device serial (LOGIN wipe rehydration). */
    vftu: {
        created: string | null;
        series: string | null;
        invoice: string | null;
        signature: string | null;
    } | null;
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _PlaceView extends ViewObject<Place> {
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get place(): Place{
        return this.object;
    }

    constructor(object: Place, data: dataService){
        super(object, data);

        // wait until the place is stablished
        this.place.OnInitialize.pipe(take(1)).subscribe(() => {
            this.Initialize();
        })
    }

    private async Initialize(): Promise <void> {
        const _state = this.data.injector.get(stateService) as stateService | null;
        if (_state?.Access === 'GUEST'){
            return;
        }

        await this.InitializeTickets();
    }

    /**********************************/
    /* INJECTOR SERVICES              */
    /**********************************/

    private _adhoc: adhocService | null = null;
    private get adhoc(): adhocService {
        if (!this._adhoc){
            this._adhoc = this.data.injector.get(adhocService);
        }
        return this._adhoc;
    }

    private _clock: clockService | null = null;
    private get clock(): clockService {
        if (!this._clock){
            this._clock = this.data.injector.get(clockService);
        }
        return this._clock;
    }

    private _i18n: languageService | null = null;
    private get i18n(): languageService {
        if (!this._i18n){
            this._i18n = this.data.injector.get(languageService);
        }
        return this._i18n;
    }

    private _toast: toastService | null = null;
    private get toast(): toastService {
        if (!this._toast){
            this._toast = this.data.injector.get(toastService);
        }
        return this._toast;
    }

    private _alertCtrl: alertService | null = null;
    private get alertCtrl(): alertService {
        if (!this._alertCtrl){
            this._alertCtrl = this.data.injector.get(alertService);
        }
        return this._alertCtrl;
    }

    private _localCfg: configService | null = null;
    private get localcfg(): configService {
        if (!this._localCfg){
            this._localCfg = this.data.injector.get(configService);
        }
        return this._localCfg;
    }

    private _catalog: Catalog | null = null;
    get catalog(): Catalog {
        if (!this._catalog){
            this._catalog = new Catalog(this.adhoc, this.clock, this.data);
        }
        return this._catalog;
    }

    private _search: SearchTool | null = null;
    get search(): SearchTool {
        if (!this._search){
            this._search = new SearchTool(this.data);
        }
        return this._search;
    }

    private _state: stateService | null = null;
    private get state(): stateService {
        if (!this._state){
            this._state = this.data.injector.get(stateService);
        }
        return this._state;
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validtables: Array <QrCodeView> | null = null;
    private _validproducts: Array <ProductView> | null = null;
    private _validfamilies: Array <FamilyView> | null = null;
    private _groupproducts: CatalogGrouped | null = null;
    private _validoffers: Array <OfferView> | null = null;
    private _validextras: Array <ExtraView> | null = null;
    private _validdiscounts: Array <DiscountView> | null = null;
    private _validemployees: Array <EmployeeView> | null = null;
    private _validclients: Array <Client> | null = null;
    private _validtickets: Array <TicketView> | null = null;
    private _validrasppi: Array <RasppiView> | null = null;
    private _validbarprinters: PrintDevice[] | null = null;
    private _validaccounts: Array <PayAccountView> | null = null;
    private _validdrawareas: Array <FloorArea> | null = null;
    private _validtills: Array <TillView> | null = null;
    private _validpriceareas: Array <PriceZoneView> | null = null;
    private _validtimes: PriceTime[] | null = null;
    private _validaudits: Array <AuditView> | null = null;
    private _validstripes: Array <Stripe> | null = null;
    private _validtransactions: Array <Payment> | null = null;
    private _alerttables = new Set <QrCode> ();
    private _payrequests: PayRequest[] | null = null;

    private _productslength = 0;

    private RefreshView(targets: string[]){
        if (targets.includes('PLACE')){
            this._validrasppi = null;
            this._validbarprinters = null;
            this._validtimes = null;
            this.DoRefreshView();
        }

        if (targets.includes('PLACEOPT')){
            this.DoRefreshView();
        }

        if (targets.includes('PLACELINK')){
            this.DoRefreshView();
        }

        if (targets.includes('QRCODE')){
            this._validtables = null;
            this._payrequests = null;
            this._RefreshAllAlerts();
            this.DoRefreshView();
        }

        if (targets.includes('ASKWAITER')){
            this._payrequests = null;
            this._RefreshAllAlerts();
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCT')){
            this._validproducts = null;
            this._validfamilies = null;
            this._groupproducts = null
            this.DoRefreshView();
        }

        if (targets.includes('FAMILY')){
            this._validfamilies = null;
            this.DoRefreshView();
        }

        if (targets.includes('OFFER')){
            this._validoffers = null; 
            this.DoRefreshView();
        }

        if (targets.includes('EXTRA')){
            this._validextras = null; 
            this.DoRefreshView();
        }

        if (targets.includes('DISCOUNT')){
            this._validdiscounts = null; 
            this.DoRefreshView();
        }

        if (targets.includes('STAFF')){
            this._validemployees = null; 
            this.DoRefreshView();
        }

        if (targets.includes('INVOICEBUSINESS')){
            this._validclients = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKET')){
            this._validtickets = null;
            this._payrequests = null;
            this.DoRefreshView();
        }

        if (targets.includes('RASPPI') || targets.includes('RASPPICONNECT')){
            this._validrasppi = null;
            this._validbarprinters = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRINTER')){
            this._validbarprinters = null;
            this.DoRefreshView();
        }

        if (targets.includes('FLOORAREA')){
            this._validdrawareas = null;
            this.DoRefreshView();
        }

        if (targets.includes('PAYACCOUNT')){
            this._validaccounts = null;
            this.DoRefreshView();
        }

        if (targets.includes('STRIPE')){
            this._validstripes = null;
            this.DoRefreshView();
        }

        if (targets.includes('PAYMENT')){
            this._validtransactions = null;
            this.DoRefreshView();
        }

        if (targets.includes('TILL')){
            this._validtills = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRICEZONE')){
            this._validpriceareas = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRICETIME')){
            this._validtimes = null;
            this.DoRefreshView();
        }

        if (targets.includes('AUDIT')){
            this._validaudits = null;
            this.DoRefreshView();
        }

        if (targets.includes('I18NVALUE')){
            this.DoRefreshView();
        }

        if (targets.includes('PINPAD')){
            this.DoRefreshView();
        }
    }

    private RefreshAlerts(qrcode: QrCode){
        const _qrcode = this.alives.get(qrcode) as QrCodeView | null;
        if (!_qrcode){
            return;
        }

        const _alert = _qrcode.IsValid && _qrcode.Attention;
        
        // include into the alert table
        if (_alert){
            if (!this._alerttables.has(qrcode)){
                this._alerttables.add(qrcode);
                this.DoRefreshView();
            }
        }

        // remove from the alert table
        else {
            if (this._alerttables.has(qrcode)){
                this._alerttables.delete(qrcode);
                this.DoRefreshView();
            }
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // register to updates on contained items
        this._subscriptions.push(
            this.place.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        )

        this._RefreshAllAlerts();
    }

    private _RefreshAllAlerts(): void {
        for (const _qrcode of this.object.qrcodes){
            this.RefreshAlerts(_qrcode);
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get user(): UserView | null {
        return this.data.alives.get(this.place.user) as UserView;
    }

    get tables(): Array <QrCodeView> {
        if (this._validtables == null){
            const _tables: Array <QrCodeView> = [];

            for (const _table of this.place.qrcodes){
                if (_table.IsValid){
                    const _tableview = this.alives.get(_table) as QrCodeView;
                    if (_tableview){
                        _tables.push(_tableview);
                    }    
                }
            }

            this._validtables = _tables;
        }

        return this._validtables;
    }

    get products(): Array <ProductView> {
        if (this._validproducts == null){
            const _products: Array <ProductView> = [];

            this._productslength = 0;
            for(const _product of this.place.products){
                if (_product.IsValid){
                    if (_product.IsCatalog && _product.last !== _product && _product.last.IsValid){
                        continue;   // skip catalog templates replaced by a local product
                    }

                    const _productview = this.alives.get(_product) as ProductView;
                    if (_productview){
                        _products.push(_productview);

                        if (!_productview.isgroup){
                            this._productslength++;
                        }
                    }    
                }
            }

            this._validproducts = _products;
        }
        return this._validproducts;
    }

    // calculated at the same time as the products list
    get ProductsLength(): number {
        return this._productslength;
    }

    /**
     * Family views for place products marked as family groups (`IsFamily`).
     * Ordering for the cart selector lives in the UI browser (active filter + sort).
     */
    get families(): Array <FamilyView> {
        if (this._validfamilies == null){
            const _families: Array <FamilyView> = [];

            for (const _product of this.products){
                if (!_product.IsFamily){
                    continue;
                }

                const _family = _product.family;
                if (_family){
                    _families.push(_family);
                }
            }

            this._validfamilies = _families;
        }

        return this._validfamilies;
    }

    get offers(): Array <OfferView> {
        if (this._validoffers == null){
            const _offers: Array <OfferView> = []; 

            for(const _offer of this.place.offers){
                if (_offer.IsValid){
                    const _offerview = this.alives.get(_offer) as OfferView;
                    if (_offerview){
                        _offers.push(_offerview);
                    }    
                }
            }

            this._validoffers = _offers;
        }

        return this._validoffers;        
    }

    get extras(): Array <ExtraView> {
        if (this._validextras == null){
            const _extras: Array <ExtraView> = []; 

            for(const _extra of this.place.extras){
                if (_extra.IsValid){
                    const _extraview = this.alives.get(_extra) as ExtraView;
                    if (_extraview){
                        _extras.push(_extraview);
                    }    
                }
            }

            this._validextras = _extras;
        }

        return this._validextras;        
    }

    get discounts(): Array <DiscountView> {
        if (this._validdiscounts == null){
            const _discounts: Array <DiscountView> = []; 

            for(const _discount of this.place.discounts){
                if (_discount.IsValid){
                    const _discountview = this.alives.get(_discount) as DiscountView;
                    if (_discountview){
                        _discounts.push(_discountview);
                    }    
                }
            }

            this._validdiscounts = _discounts;
        }

        return this._validdiscounts;        
    }

    get employees(): Array <EmployeeView> {
        if (this._validemployees == null){
            const _employees: Array <EmployeeView> = []; 

            for(const _employee of this.place.employees){
                if (_employee.IsValid){
                    const _employeeview = this.alives.get(_employee) as EmployeeView;
                    if (_employeeview){
                        _employees.push(_employeeview);
                    }    
                }
            }

            this._validemployees = _employees;
        }

        return this._validemployees;        
    }

    get clients(): Array <Client> {
        if (this._validclients === null){
            const _clients: Array <Client> = [];

            for (const _client of this.place.clients){
                if (_client.IsValid){
                    _clients.push(_client);
                }
            }

            this._validclients = _clients;
        }

        return this._validclients;
    }

    get accounts(): Array <PayAccountView> {
        if (this._validaccounts === null){
            const _accounts: Array <PayAccountView> = [];

            for (const _account of this.place.accounts){
                if (_account.IsValid){
                    const _accountview = this.alives.get(_account) as PayAccountView;
                    if (_accountview){
                        _accounts.push(_accountview);
                    }
                }
            }

            _accounts.sort((a, b) => {
                const _aname = a.name ?? '';
                const _bname = b.name ?? '';
                if (_aname === _bname){
                    return 0;
                }

                return (_aname < _bname) ? -1 : 1;
            });

            this._validaccounts = _accounts;
        }

        return this._validaccounts;
    }

    /** Stripe Connect rows for this place (DataObject list; no dedicated View). Excludes `DE` via {@link Stripe.IsValid}. */
    get stripes(): Array <Stripe> {
        if (this._validstripes === null){
            const _stripes: Array <Stripe> = [];
            for (const _stripe of this.place.stripes || []){
                if (_stripe.IsValid){
                    _stripes.push(_stripe);
                }
            }

            this._validstripes = _stripes;
        }

        return this._validstripes;
    }

    get stripe(): Stripe | null {
        return this.stripes.find((row) => !!row?.IsActive) ?? null;
    }

    get CanLine(): boolean {
        return this.place.CanLine && !!this.stripe;
    }

    /**
     * Whether cash is allowed in this place for the current device.
     * Place option ({@link Place.CanCash}) plus ptf `nocash` on the linked
     * terminal — outside ptf or without a link, only the place option applies.
     */
    get CanCash(): boolean {
        if (this.OwnPinpad?.nocash === true){
            return false;
        }

        return this.place.CanCash;
    }

    /**
     * Whether a cash payment should request a receipt (and open the drawer in POS).
     * On `ptf` the linked terminal's `oncash` is the source of truth (false when
     * unlinked). Outside `ptf`, place config ({@link Place.OpenOnCash}).
     */
    get OpenOnCash(): boolean {
        if (this.state.WorkMode === 'PTF'){
            return this.OwnPinpad?.oncash === true;
        }

        return this.place.OpenOnCash;
    }

    /**
     * Whether a card payment should request a receipt (and open the drawer in POS).
     * On `ptf` the linked terminal's `oncard` is the source of truth (false when
     * unlinked). Outside `ptf`, place config ({@link Place.OpenOnCard}).
     */
    get OpenOnCard(): boolean {
        if (this.state.WorkMode === 'PTF'){
            return this.OwnPinpad?.oncard === true;
        }

        return this.place.OpenOnCard;
    }

    /**
     * How «pay by card» is collected for the current app + device.
     * Independent of `optionCardModal`. Inventory predicate only —
     * the UI picks the destination terminal per payment (`OwnPinpad` / `pinpads` / picker).
     * - `TERMINAL` — charge/refund on a Paytef pinpad (linked on ptf; pick on POS).
     * - `UNLINKED` — ptf with place terminals but this device is not linked.
     * - `GENERIC` — no pinpad path (no inventory, or QRA): classic card intent.
     */
    get CardMethod(): 'TERMINAL' | 'UNLINKED' | 'GENERIC' {
        if (this.pinpads.length === 0){
            return 'GENERIC';
        }

        if (this.state.WorkMode === 'PTF'){
            return this.OwnPinpad ? 'TERMINAL' : 'UNLINKED';
        }

        if (this.state.WorkMode === 'POS'){
            return 'TERMINAL';
        }

        return 'GENERIC';
    }

    /** Payment rows for this place (license and ticket); filter by ticket in UI. */
    get transactions(): Array <Payment> {
        if (this._validtransactions === null){
            this._validtransactions = [...(this.place.transactions || [])];
        }
        
        return this._validtransactions;
    }

    get audits(): Array <AuditView> {
        if (this._validaudits === null){
            const _audits: Array <AuditView> = [];

            for (const _audit of this.place.audits || []){
                const _view = this.alives.get(_audit) as AuditView | null;
                if (_view){
                    _audits.push(_view);
                }
            }

            _audits.sort((a, b) => {
                return  (b.end?.getTime() ?? 0) - (a.end?.getTime() ?? 0);
            });

            this._validaudits = _audits;
        }

        return this._validaudits;
    }

    get tickets(): Array <TicketView> {
        if (this._validtickets === null){
            const _validtickets: Array <TicketView> = [];

            for (const _ticket of this.place.tickets){
                if (_ticket.IsValid){
                    const _ticketview = this.alives.get(_ticket) as TicketView;
                    if (_ticketview){
                        _validtickets.push(_ticketview);
                    }
                }
            }

            _validtickets.sort((a, b) => {
                const _acreated = (a.created || a.updated || new Date(0)).getTime();
                const _bcreated = (b.created || b.updated || new Date(0)).getTime();

                return (_bcreated - _acreated);
            })

            this._validtickets = _validtickets;
        }

        return this._validtickets;
    }

    get DrawAreas(): FloorArea[] {
        if (this._validdrawareas === null){
            this._validdrawareas = [];

            for (const _area of this.place.drawareas){
                if (_area.IsValid){
                    this._validdrawareas.push(_area);
                }
            }
        }

        return this._validdrawareas;
    }

    /** Price zones defined for this place. */
    get PriceAreas(): PriceZoneView[] {
        if (this._validpriceareas === null){
            const _pricezones: PriceZoneView[] = [];

            for (const _pricezone of this.place.pricezones || []){
                if (!_pricezone.IsValid){
                    continue;
                }

                const _view = this.alives.get(_pricezone) as PriceZoneView | null;
                if (_view){
                    _pricezones.push(_view);
                }
            }

            this._validpriceareas = _pricezones;
        }

        return this._validpriceareas;
    }

    /** Valid price periods for this place, sorted by `sort` ascending. */
    get PriceTimes(): PriceTime[] {
        if (this._validtimes === null){
            const _periods: PriceTime[] = [];

            for (const _period of this.place.pricetimes ?? []){
                if (_period.IsValid){
                    _periods.push(_period);
                }
            }

            this._validtimes = _periods.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        }

        return this._validtimes;
    }

    /**
     * Among valid periods active at `at`, returns the one with the lowest `sort`
     * (first in list order wins); `null` when none applies.
     */
    ActiveTime(at: Date): PriceTime | null {
        let _best: PriceTime | null = null;
        let _bestSort = Number.POSITIVE_INFINITY;

        for (const _period of this.PriceTimes){
            if (!_period.IsActiveAt(at)){
                continue;
            }

            const _sort = _period.sort ?? 0;
            if (_sort < _bestSort){
                _bestSort = _sort;
                _best = _period;
            }
        }

        return _best;
    }

    /** Operational tills on this place */
    get TillAreas(): TillView[] {
        if (this._validtills === null){
            const _tills: TillView[] = [];

            for (const _till of this.place.tills || []){
                if (!_till.IsValid){
                    continue;
                }

                const _view = this.alives.get(_till) as TillView | null;
                if (_view){
                    _tills.push(_view);
                }
            }

            this._validtills = _tills;
        }

        return this._validtills;
    }

    /** Whether the session device operates as TPV on this place. */
    get IsPos(): boolean {
        return this.place.IsPos;
    }

    /**********************************/
    /* TABLES REQUIRING WAITER        */
    /**********************************/

    get AlertSize(): number {
        return this._alerttables.size;
    }

    get AlertTables(): Array <QrCode> {
        return [...this._alerttables]
    }

    /**
     * Pending guest payment requests across tables of this place (one per mesa).
     * Ordered by oldest ask first (`since` ascending).
     */
    get PayRequests(): PayRequest[] {
        if (this._payrequests === null){
            const _list: PayRequest[] = [];
            for (const _table of this.tables){
                const _request = _table.PayRequest;
                if (_request){
                    _list.push(_request);
                }
            }

            _list.sort((a, b) => {
                const _ta = a.since?.getTime() ?? Number.POSITIVE_INFINITY;
                const _tb = b.since?.getTime() ?? Number.POSITIVE_INFINITY;
                return _ta - _tb;
            });

            this._payrequests = _list;
        }

        return this._payrequests;
    }

    /**********************************/
    /* PLACE SERVICES                 */
    /**********************************/

    /**
     * Session device active connect on this place (`PosDevice.connect`).
     * Same source as `RequiresLocalBackup` / TPV gates — do not re-scan services.
     */
    get connect(): RasppiConnect | null {
        return this.data.session?.device?.connect ?? null;
    }

    /** RASPPI linked to the session device active connect, when any. */
    get service(): RasppiView | null {
        const _connect = this.connect;
        if (_connect){
            const _rasppi = _connect.rasppi;
            if (_rasppi?.IsValid){
                return this.alives.get(_rasppi) as RasppiView;
            }
        }

        return null;
    }

    get HasCodeScan(): boolean {
        return (this.service?.codescans.length || 0) > 0;
    }

    get HasScale(): boolean {
        return (this.service?.scales.length || 0) > 0;
    }

    get OwnPinpad(): Pinpad | null {
        if (this.state.WorkMode !== 'PTF'){
            return null;
        }

        if (this.pinpads.length === 0){
            return null;
        }

        return this.data.session?.device?.pinpad ?? null;
    }

    /**
     * All sessions of this place: owner USER, STAFF users, and guest QRSCAN
     * sessions. Deduped by instance. No mode/status filter — callers decide.
     */
    get sessions(): Session[] {
        const _seen = new Set<Session>();

        const _Add = (list: Session[] | null | undefined): void => {
            for (const _session of list ?? []){
                if (_session){
                    _seen.add(_session);
                }
            }
        };

        _Add(this.place.user?.sessions);
        for (const _employee of this.place.employees){
            if (_employee.IsValid){
                _Add(_employee.user?.sessions);
            }
        }

        for (const _table of this.place.qrcodes){
            _Add(_table.qrscan?.sessions);
        }

        return [ ..._seen ];
    }

    /** Payment terminals aggregated from every print service of this place. */
    get pinpads(): Pinpad[] {
        const _terminals: Pinpad[] = [];
        for (const _service of this.services){
            for (const _terminal of _service.pinpads){
                _terminals.push(_terminal);
            }
        }
        return _terminals;
    }
    
    /** BAR printers on the session TPV service (cash drawer targets). */
    get BarPrinters(): PrintDevice[] {
        if (this._validbarprinters === null){
            const _printers: PrintDevice[] = [];
            const _service = this.service;

            if (_service){
                for (const _printer of _service.printers){
                    if (_printer.type === 'BAR'){
                        _printers.push(_printer);
                    }
                }
            }

            this._validbarprinters = _printers;
        }

        return this._validbarprinters;
    }

    get services(): Array<RasppiView> {
        if (this._validrasppi == null){
            const _rasppi: Array<RasppiView> = [];
            for (const _service of this.place.services){
                if (_service.IsValid){
                    const _view = this.alives.get(_service) as RasppiView;
                    if (_view){
                        _rasppi.push(_view);
                    }
                }
            }
            this._validrasppi = _rasppi;
        }

        return this._validrasppi;
    }


    /**********************************/
    /* TICKET GENERATION              */
    /**********************************/    

    private _ticketsconfig: Configuration | null = null;
    /** Resolves true when device/getticket merge completed; false when reconcile failed. */
    private _ticketsready: Promise<boolean> = Promise.resolve(false);

    private get ticketstore(){
        if ((this._ticketsconfig == null)){
            this._ticketsconfig = this.localcfg.getConfiguration(this.place.objid + '_DEVICE_TICKETS');
        }
        return this._ticketsconfig;
    }

    private async _LastServerTickets(): Promise <TicketDeviceInfo | null> {
        const _storedata: TicketDeviceInfo = {
            X: null,
            T: null,
            F: null,
            R: null,
            A: null,
            C: null,

            tbai: null,
            vftu: null
        };

        let _storeready = false;

        const _getTicketUrl = 'device/getticket';
        try {
            console.info("[DEVICE TICKET] Requesting server information..")
            const data = await this.adhoc.DoRequest(_getTicketUrl, { serial: this.data.serial }, null);

            _storeready = (data['errorcode'] == 0);
            if (_storeready){
                for (const type of Object.values(InvoiceType)){
                    if (_storeready){
                        _storedata[type] = (data.last && data.last[type] !== undefined) ? data.last[type] : null;
                        if (_storedata[type] === null){
                            _storeready = false;
                        }
                        else {
                            // Math.max: never rewind a local-ahead counter; never keep a stale local behind the server.
                            const _local = Number(this.ticketstore.get(type) ?? 0) || 0;
                            const _remote = Number(_storedata[type] ?? 0) || 0;
                            const _merged = Math.max(_local, _remote);
                            _storedata[type] = _merged;
                            this.ticketstore.set(type, _merged);
                        }
                    }    
                }                    

                // LOGIN chain rehydration after wipe (device/getticket): TBAI + VFTU.
                _storedata.tbai = (data.tbai || null) as TicketDeviceInfo['tbai'];
                if (_storedata.tbai){
                    this.ticketstore.set('tbai', _storedata.tbai);
                }
                _storedata.vftu = (data.vftu || null) as TicketDeviceInfo['vftu'];
                if (_storedata.vftu){
                    this.ticketstore.set('vftu', _storedata.vftu);
                }
            }
        }
        catch(error: any){
            if (error && error.status) {
                console.error("[REQUEST] Error [" + error.status + "] in http request '" + _getTicketUrl + "'");
            } 
            else {
                console.error("[REQUEST] Generic error in http request '" + _getTicketUrl + "'");
            }            
        }

        return _storeready ?  _storedata : null
    }

    private async InitializeTickets(): Promise <void> {
        if (!this.place.objid){
            return;   // place not created yet: do not request ticket info
        }

        // Always reconcile with the server (even when local looks complete).
        // The in-flight promise is the gate NextDeviceTicket awaits for fiscal series.
        this._ticketsready = this._LastServerTickets().then((_data) => _data != null);
        const _reconciled = await this._ticketsready;

        if (!_reconciled){
            let _haslocal = true;
            for (const type of Object.values(InvoiceType)){
                if ((this.ticketstore.get(type) ?? null) === null){
                    _haslocal = false;
                    break;
                }
            }
            if (!_haslocal){
                throw new Error("[DEVICE TICKET] Cannot recover sent tickets from server side!")
            }
        }
        else {
            for (const type of Object.values(InvoiceType)){
                const _value = this.ticketstore.get(type);
                console.info("[DEVICE TICKET] Last served: " + type + ("00000000" + _value).slice(-8));
            }
        }
    }

    async NextDeviceTicket(serialtype: SerialType, invoicetype: InvoiceType): Promise <string[]> {
        // Fiscal series must wait for max(local, server). Provisional X may proceed.
        if (invoicetype !== InvoiceType.X){
            const _reconciled = await this._ticketsready;
            if (!_reconciled){
                if (this.localcfg.writefailed(this.place.objid + '_DEVICE_TICKETS')){
                    void this.toast.ShowAlert('danger', this.i18n.tr('@place_ticket_create_error'));
                    throw new Error("[DEVICE TICKET] storage write failed and server reconcile unavailable");
                }
                console.warn("[DEVICE TICKET] Server reconcile unavailable; issuing from local counters.");
            }
        }

        let lastinvoice = (this.ticketstore.get(invoicetype) ?? null) as number | null;
        if (lastinvoice === null){
            void this.toast.ShowAlert('danger', this.i18n.tr('@place_ticket_create_error'));
            throw new Error("[DEVICE TICKET] counter not initialized for type '" + invoicetype + "'");
        }

        this.ticketstore.set(invoicetype, ++lastinvoice);   // increase and store

        const nextserial = serialtype + this.data.serial;
        const nextinvoice = invoicetype + lastinvoice.toString().padStart(8, '0');

        return [ nextserial, nextinvoice ];        
    }

    /**********************************/
    /* CUSTOM ACTIONS                 */
    /**********************************/    

    /* table creation and deletion */

    private get nextno(){
        let _next = 1;
        
        for (const _table of this.place.qrcodes){
            if (_table.IsValid){
                _next++;
            }
        }

        return _next;   // this is orientative and will be replaced in the server
    }

    async LoadCatalog(){
        if (!this.catalog.IsLoaded) {
            this.toast.ShowWait();
            await this.catalog.DoLoad(this.object);
            this.toast.HideWait();
        }
    }

    CreateTables(size: number): void {
        for(let _idx=0; _idx < size; _idx++){
            const _table = new QrCode(null, this.data);
            if (_table){
                _table.place = this.place;
                _table.number = this.nextno;
                _table.status = 'PA';
                _table.chairs = 4;
            }

            this.place.AddQrcode(_table);
        }
        
        this.place.DoCommit();
    }

    DeleteTables(size: number): void {
        const _tables: Array<QrCode> = [];

        // filter only valid (not deleted) tables
        for (const _table of this.place.qrcodes){
            if (_table.IsValid) {
                _tables.push(_table);
            }
        }

        // order the tables by the provided number
        _tables.sort((a, b) => {
            return Number(a.number) - Number(b.number);
        });

        // delete the first (size) tables
        for(let _idx=0; _idx < size; _idx++){
            _tables[_idx].status = 'DE';
        }

        this.place.DoCommit();
    }

    /* place product grouping */
    get GroupedProducts(): CatalogGrouped {
        if (this._groupproducts == null){
            const _groupproducts = new Map <ProductView | null, ProductView[]> ();
            const _productscopy = this.products.slice(0);

            // create the empty groups array 
            for(const _product of _productscopy){
                if (_product.IsGroup){
                    if (_product.IsCatalog && _product.last && _product.last !== _product){
                        continue;   // skip catalog groups replaced by a local group
                    }

                    _groupproducts.set(_product, []);
                }
            }

            _groupproducts.set(null, []);

            // add the children to the parents
            for(const _product of _productscopy){
                if (!_product.IsGroup && _product.IsCatalog && _product.last && _product.last !== _product){
                    continue;   // skip catalog templates replaced by a local product
                }

                const _parent = _product.parent;
                const _children = _groupproducts.get(_parent);
                if (_children){
                    _children.push(_product);
                }
                else {
                    console.error("[PLACE VIEW] Cannot group product: Parent not found.", _product.object)
                }

                // sort the children: Families -> Groups -> Other
                if (_children){
                    _children.sort((a, b) => {
                        if (a.IsFamily && !b.IsFamily){
                            return -1;
                        }

                        if (!a.IsFamily && b.IsFamily){
                            return 1;
                        }

                        if (a.IsGroup && !b.IsGroup){
                            return -1;
                        }

                        if (!a.IsGroup && b.IsGroup){
                            return 1;
                        }

                        return (a.sort || 0) - (b.sort || 0);
                    })
                }
            }

            // convert map to array
            this._groupproducts = Array.from(_groupproducts.entries()).map(([parent, children]) => ({ parent, children }));
        }

        return this._groupproducts;
    }

    /**********************************/
    /* PRODUCT/TABLE SEARCH           */
    /**********************************/    

    doSearch(criteria: string): Array <ProductView | PreselectView>{
        return this.search.doSearch(criteria, this.products, AppConstants.SearchThreshold);
    }

    doBarCode(code: string): Array<ProductView | PreselectView> {
        const _matches: Array<ProductView | PreselectView> = [];

        if (!code){
            return [];
        }

        for (const _product of this.products){
            if (_product.isgroup || _product.IsCatalog || !_product.IsAvailable){
                continue;
            }

            if (_product.barcode === code){
                _matches.push(_product);
            }

            for (const _select of _product.selects){
                if (!_select.IsAvailable){
                    continue;
                }

                if (_select.barcode === code){
                    _matches.push(_select);
                }
            }
        }

        return _matches;
    }

    doPrdCode(pattern: string): Array<ProductView | PreselectView> {
        const _matches: Array<ProductView | PreselectView> = [];
        const _pattern = pattern.trim();

        if (!_pattern){
            return [];
        }

        for (const _product of this.products){
            if (_product.isgroup || _product.IsCatalog || !_product.IsAvailable){
                continue;
            }

            const _productCode = _product.code?.trim() || '';
            if (_productCode.startsWith(_pattern)){
                _matches.push(_product);
            }

            for (const _select of _product.selects){
                if (!_select.IsAvailable){
                    continue;
                }

                const _selectCode = _select.code?.trim() || '';
                if (_selectCode.startsWith(_pattern)){
                    _matches.push(_select);
                }
            }
        }

        return _matches.sort((left, right) => {
            const _leftCode = (left.code?.trim() || '');
            const _rightCode = (right.code?.trim() || '');
            if (_leftCode === _rightCode){
                return 0;
            }

            return _leftCode < _rightCode ? -1 : 1;
        });
    }

    doTblNumber(pattern: string): Array<QrCodeView> {
        const _pattern = pattern.trim();

        if (!_pattern){
            return [];
        }

        const _matches: Array<QrCodeView> = [];
        for (const _table of this.tables){
            const _number = _table.number;
            if (_number === null || _number === undefined){
                continue;
            }

            if (_number.toString().startsWith(_pattern)){
                _matches.push(_table);
            }
        }

        return _matches.sort((left, right) => (left.number ?? 0) - (right.number ?? 0));
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type PlaceView = ViewType <_PlaceView, Place>;

export function PlaceViewProxy(object: Place, data: dataService): PlaceView {
    return new _PlaceView(object, data).Proxy as PlaceView;
}